import { notFound, redirect } from "@tanstack/react-router";
import { ApiError } from "@softmax/observatory-client/transport";
import { resolveAdmissionDecision } from "./admission/admission";
import { resolveGameAddress } from "./game-address/read";
import { getActualViewer } from "@/data/viewer";
import { gameNavigationHref } from "../game-types";
import { type GameContextRead, type GamePageContext } from "../game-types";

export async function readGameContext({
  signal,
  href,
}: {
  signal?: AbortSignal | undefined;
  href: string;
}): Promise<GameContextRead> {
  const resolution = await resolveGameAddress({
    signal,
    href,
  });

  if (resolution.kind === "navigate") {
    if (resolution.navigation.kind === "sign-in")
      return { ...resolution, viewer: await getActualViewer({ signal }) };

    return resolution;
  }

  if (resolution.kind === "unavailable") {
    throw new ApiError("The game is temporarily unavailable", 503);
  }

  if (resolution.kind === "invalid") {
    throw new ApiError("The game address is invalid", 400);
  }

  if (resolution.kind !== "resolved") throw notFound();

  const admission = await resolveAdmissionDecision({
    signal,
    href,
    game: resolution.context,
  });

  if (admission.kind === "navigate")
    return { ...admission, viewer: resolution.context.viewer };

  if (admission.kind === "not-found") throw notFound();

  if (admission.kind === "unavailable") {
    throw new ApiError(admission.message, 503);
  }

  const game = admission.presentation
    ? { ...resolution.context, presentation: admission.presentation }
    : resolution.context;
  return { kind: "ready", game };
}

export async function requireGameContext({
  signal,
  href,
}: {
  signal?: AbortSignal | undefined;
  href: string;
}): Promise<GamePageContext> {
  const result = await readGameContext({
    signal,
    href,
  });

  if (result.kind === "navigate")
    throw redirect({
      href: gameNavigationHref({ navigation: result.navigation, href }),
      statusCode:
        result.navigation.kind === "path" ? result.navigation.status : 307,
    });

  return { ...result.game, publicPath: href };
}

export async function requireGameResource({
  signal,
  href,
}: {
  signal?: AbortSignal | undefined;
  href: string;
}): Promise<GamePageContext> {
  const result = await readGameContext({
    signal,
    href,
  });

  if (result.kind === "navigate")
    throw new ApiError(
      "The game address must be opened before its content",
      result.navigation.kind === "sign-in" ? 401 : 404,
    );

  return result.game;
}
